import formatUTCDate from '@/shared/tools/formatUTCDate'

export const mapCommonSubscriptionData = viewParameters => {
  const currentContract = viewParameters?.current_subscriber ?? {}

  return {
    contractLoaded: true,
    customer: retrieveCustomerData(viewParameters),
    billing: retrieveBillingData(currentContract),
    mainAddress: retrieveMainAddressData(viewParameters, currentContract),
    invoiceAddress: retrieveInvoiceAddressData(viewParameters),
    residenceAddress: retrieveResidenceAddressData(viewParameters, currentContract),
    identity: mapIdentityData(viewParameters),
  }
}

export const retrieveInvoiceAddressData = viewParameters => {
  const invoicingAddress = viewParameters.invoicing_address ?? {}

  if (Object.keys(invoicingAddress).length === 0) {
    return {
      showCheckbox: false,
      address: '',
      streetNumber: '',
      city: '',
      inseeCode: '',
      country: '',
      zipcode: '',
      id: '',
      residenceType: '',
      housingType: '',
      tenantType: '',
      floor: '',
      appartmentNumber: '',
      additionalInformation: '',
    }
  }

  return {
    showCheckbox: true,
    address: invoicingAddress.street_name,
    streetNumber: invoicingAddress.street_number,
    city: invoicingAddress.city,
    inseeCode: invoicingAddress.city_id,
    country: invoicingAddress.country,
    zipcode: invoicingAddress.zipcode,
    id: invoicingAddress.city_id,
    residenceType: invoicingAddress.residence_type ?? '',
    housingType: invoicingAddress.housing_type ?? '',
    tenantType: invoicingAddress.tenant_type ?? '',
    floor: invoicingAddress.floor ?? '',
    appartmentNumber: invoicingAddress.appartment_number ?? '',
    additionalInformation: invoicingAddress.additional_information ?? '',
  }
}

export const retrieveMainAddressData = (viewParameters, currentContract) => {
  const mainAddress = viewParameters.main_address ?? {}
  if (Object.keys(mainAddress).length === 0) {
    return {
      address: '',
      streetNumber: '',
      label: '',
      city: '',
      inseeCode: '',
      city_id: '',
      province: '',
      id: '',
      zipcode: '',
      housingType: '',
      household: '',
      occupancy: '',
      floor: '',
      tenantType: '',
      residenceType: '',
      appartmentNumber: '',
      additionalInformation: '',
      movingStartDate: '',
      providedByGas: '',
      cadastralParcelNumber: '',
      cadastralSheetNumber: '',
      cadastralSubunitNumber: '',
      lastHolderName: '',
      lastHolderCf: '',
      lastHolderPropertyDate: '',
    }
  }

  const streetNumber = mainAddress.street_number ? mainAddress.street_number + ' ' : ''
  const streetName = mainAddress.street_name ?? ''
  const zipcode = mainAddress.zipcode ?? ''
  const city = mainAddress.city ?? ''
  const province = mainAddress.province ?? ''
  const cadastralData = mainAddress.cadastral_data ?? {}
  const lastHolderData = mainAddress.last_holder_data ?? {}
  const {
    building_permit_option: buildingPermitOption,
    building_permit_number: buildingPermitNumber,
    building_permit_created_date: buildingPermitDate,
    building_permit_issuing_city: buildingPermitCity,
    building_permit_issuing_entity: buildingPermitEntity,
  } = mainAddress.building_permit_data ?? {}

  return {
    address: mainAddress.street_name,
    streetNumber,
    label: streetNumber + streetName + ' ' + zipcode + ' ' + city,
    city: mainAddress.city,
    cityId: currentContract.city_id,
    province,
    inseeCode: currentContract.city_id,
    id: currentContract.city_id,
    zipcode: mainAddress.zipcode,
    isResident: mainAddress.is_resident?.toString() ?? '',
    floor: mainAddress.floor ?? '',
    appartmentNumber: mainAddress.appartment_number ?? '',
    residenceType: mainAddress.residence_type ?? '',
    housingType: mainAddress.housing_type ?? '',
    tenantType: mainAddress.tenant_type ?? '',
    additionalInformation: mainAddress.additional_information ?? '',
    household: mainAddress.household ?? '',
    occupancy: mainAddress.occupancy ?? '',
    movingStartDate: mainAddress.moving_start_date ?? '',
    cadastralParcelNumber: cadastralData?.cadastral_parcel_number ?? '',
    cadastralSheetNumber: cadastralData?.cadastral_sheet_number ?? '',
    cadastralSubunitNumber: cadastralData?.cadastral_subunit_number ?? '',
    lastHolderName: lastHolderData?.last_holder_name ?? '',
    lastHolderCf: lastHolderData?.last_holder_cf ?? '',
    lastHolderPropertyDate: lastHolderData?.last_holder_property_date ?? '',
    buildingPermitOption,
    buildingPermitNumber,
    buildingPermitDate,
    buildingPermitEntity,
    buildingPermitCity,
  }
}

export const retrieveResidenceAddressData = (viewParameters, currentContract) => {
  const residenceAddress = viewParameters.residence_address ?? {}
  if (Object.keys(residenceAddress).length === 0) {
    return {
      optionChecked: false,
      address: '',
      streetNumber: '',
      label: '',
      city: '',
      province: '',
      zipcode: '',
      floor: '',
      appartmentNumber: '',
      additionalInformation: '',
    }
  }

  const streetNumber = residenceAddress.street_number ? residenceAddress.street_number + ' ' : ''
  const streetName = residenceAddress.street_name ?? ''
  const zipcode = residenceAddress.zipcode ?? ''
  const city = residenceAddress.city ?? ''
  const province = residenceAddress.province ?? ''

  return {
    optionChecked: true,
    address: residenceAddress.street_name,
    streetNumber,
    label: streetNumber + streetName + ' ' + zipcode + ' ' + city,
    city: residenceAddress.city,
    province,
    zipcode: residenceAddress.zipcode,
    floor: residenceAddress.floor ?? '',
    appartmentNumber: residenceAddress.appartment_number ?? '',
    additionalInformation: residenceAddress.additional_information ?? '',
  }
}

export const retrieveBillingData = currentContract => {
  return {
    iban: currentContract.banking?.iban ?? '',
    bic: currentContract.banking?.bic ?? '',
    businessCard: currentContract.business_card === 1,
    realPayment: currentContract.banking?.payment_mode_real ?? '',
    paymentMode: currentContract.banking?.payment_mode ?? '-1',
    paymentModeAlt: currentContract.banking?.payment_mode_alt ?? '',
    frequency: currentContract.banking?.invoice_frequency ?? '-1',
    sendingMode: currentContract.banking?.invoice_sending_mode ?? '-1',
    paymentDate: currentContract.banking?.payment_date ?? '',
    differentAccountHolder:
      currentContract.banking?.owner_lastname && currentContract.lastname !== currentContract.banking?.owner_lastname,
    accountHolderFirstname: currentContract.banking?.owner_lastname ?? '',
    accountHolderLastname: currentContract.banking?.owner_firstname ?? '',
    accountHolderTaxIdCode: currentContract.banking?.accountHolderTaxIdCode ?? '',
    promoCode: currentContract.banking?.promotional_code ?? '',
    providersBlacklist: JSON.parse(currentContract.product_energy?.blacklist_providers ?? null), // TO DO: check if we have to move it in energy contract instead as stored in product_energy
  }
}

export const retrieveCustomerData = viewParameters => {
  const currentContract = viewParameters?.current_subscriber ?? {}
  return {
    zohoId: currentContract.zoho_id ?? '',
    name: currentContract.firstname ?? '',
    name2: currentContract.firstname2 ?? '',
    surname: currentContract.lastname ?? '',
    surname2: currentContract.lastname2 ?? '',
    email: currentContract.email ?? '',
    neverbounce_result: currentContract.email?.length > 0 ? 'valid' : '',
    secondName: currentContract.firstname2?.length > 0 || currentContract.lastname2?.length > 0,
    dataCollectionAuth: currentContract.disclaimer === 1,
    civility: currentContract.civility ?? '',
    civility2: currentContract.civility2 ?? '',
    phone1: currentContract.phone1 ?? '',
    phone2: currentContract.phone2 ?? '',
    birthdate: currentContract.birthday ?? '',
    referralFirstName: currentContract.referral_firstname ?? '',
    referralSecondName: currentContract.referral_lastname ?? '',
    isNewsletterRegistered: currentContract.newsletter,
    bacVoucher: currentContract.bac_voucher ?? false,
    isReviewRegistered: currentContract.review_interaction,
    isClubSelectraRegistered: currentContract.club_selectra_interaction?.length > 0,
    pro_social_reason: currentContract.pro_social_reason ?? '',
    pro_siret: currentContract.pro_siret ?? '',
    pro_activity: currentContract.pro_activity ?? '',
    tax_id_code: currentContract.banking?.tax_id_code ?? '',
    bornCity: currentContract.born_city ?? '',
    hasAlreadyVotedFeedback: viewParameters?.has_already_voted_feedback ?? false,
    pro_ape_code: currentContract.pro_ape_code ?? '',
    web_callback: currentContract.web_callback
      ? {
          callbackDate: currentContract.web_callback.split('T')[0] ?? '',
          callbackTime: currentContract.web_callback.split('T')[1] ?? '',
        }
      : { callbackDate: '', callbackTime: '' },
    comments: getLastComment(currentContract.comments ?? []),
  }
}

export const filterLastComment = comments => {
  if (!comments.isArray && typeof comments === 'string') {
    return comments
  }
  let lastCommentTime = new Date(comments[0].created_at).getTime()
  let lastComment = comments[0].comment
  // eslint-disable-next-line func-names
  comments.forEach(function (item) {
    if (new Date(item.created_at).getTime() >= lastCommentTime) {
      lastCommentTime = new Date(item.created_at).getTime()
      lastComment = item.comment
    }
  })
  return lastComment ?? ''
}
export const getLastComment = comments => {
  if (comments.length === 0) {
    return ''
  }
  if (comments.length === 1) {
    return comments[0].comment ?? ''
  }
  return filterLastComment(comments)
}

export const mapIdentityData = viewParameters => {
  const identityInfo = viewParameters.italy_identity_information ?? {}

  return {
    documentType: identityInfo?.document_type ?? '',
    documentNumber: identityInfo?.document_number ?? '',
    expirationDate: identityInfo?.expiration_date ? formatUTCDate(identityInfo?.expiration_date, 10) : '',
    createdDate: identityInfo?.created_date ? formatUTCDate(identityInfo?.created_date, 10) : '',
    issuingEntity: identityInfo?.issuing_entity ?? '',
    issuingCity: identityInfo?.issuing_city ?? '',
  }
}
